import React, { useMemo, useEffect } from 'react';
import { filter } from 'lodash';
import PropTypes from 'prop-types';
import { Controller, useFormContext } from 'react-hook-form';
import { Autocomplete, Box, TextField } from '@availity/element';

import { useAppContext } from '@/context';
import { errorMessages } from '@/global';

const getFieldAttrs = (questionId) => {
  const stringIndex = 7; // assuming IDToken1 syntax, need to get the number at the 7th index of the string.
  const questionIndex = questionId.charAt(stringIndex);

  const output = {
    label: `Security Question ${questionIndex}`,
    answerLabel: `Answer ${questionIndex}`,
    placeholder: `Select an option for security question ${questionIndex}.`,
    placeholderAnswer: `Enter your answer for question ${questionIndex}.`,
  };

  return output;
};

const SecurityQuestionFieldGroup = ({ questionId, answerId, registrationValues = {} }) => {
  const { register, formState = {}, control, watch, trigger } = useFormContext();
  const { errors } = formState;
  const { queryParams, securityQuestionsOptions } = useAppContext();
  const {
    label: questionLabel,
    answerLabel,
    placeholder: placeholderQuestion,
    placeholderAnswer,
  } = getFieldAttrs(questionId);

  const { userId } = queryParams;

  const { newUserEnrollment = {} } = registrationValues;
  const [IDToken1question, IDToken1answer, IDToken2question, IDToken2answer, IDToken3question, IDToken3answer] = watch([
    'IDToken1question',
    'IDToken1answer',
    'IDToken2question',
    'IDToken2answer',
    'IDToken3question',
    'IDToken3answer',
  ]);

  // get taken questions and answers
  const takenQuestions = useMemo(() => {
    const output = [];

    if (IDToken1question && IDToken1question !== '') {
      output.push(IDToken1question);
    }

    if (IDToken2question && IDToken2question !== '') {
      output.push(IDToken2question);
    }

    if (IDToken3question && IDToken3question !== '') {
      output.push(IDToken3question);
    }

    return output;
  }, [IDToken1question, IDToken2question, IDToken3question]);

  // filter out taken questions to get the options to use
  const optionsToUse = useMemo(() => {
    const output = filter(securityQuestionsOptions, (option) => takenQuestions.indexOf(option?.value) < 0);

    return output;
  }, [takenQuestions, securityQuestionsOptions]);

  // trigger validation of other answers to live-check dupes
  useEffect(() => {
    if (answerId === 'IDToken1answer' && IDToken2answer !== '' && IDToken3answer !== '') {
      trigger(['IDToken2answer', 'IDToken3answer']);
    } else if (answerId === 'IDToken2answer' && IDToken1answer !== '' && IDToken3answer !== '') {
      trigger(['IDToken1answer', 'IDToken3answer']);
    } else if (answerId === 'IDToken3answer' && IDToken1answer !== '' && IDToken2answer !== '') {
      trigger(['IDToken1answer', 'IDToken2answer']);
    }
  }, [answerId, IDToken1answer, IDToken2answer, IDToken3answer, trigger]);

  return (
    <Box id={`${questionId}-group`}>
      <Controller
        name={questionId}
        control={control}
        render={({ field: { onChange, onBlur, value } }) => (
          <Autocomplete
            id={`autocomplete-${questionId}`}
            onChange={(_, value, reason) => {
              if (reason === 'clear') {
                onChange(null);
              }
              onChange(value);
            }}
            value={value || null}
            onBlur={onBlur}
            FieldProps={{
              id: questionId,
              label: questionLabel,
              placeholder: placeholderQuestion,
              error: !!errors[questionId],
              helperText: errors[questionId]?.message,
              sx: { marginBottom: 0 },
            }}
            options={optionsToUse.map((option) => option.value)}
            disableClearable
          />
        )}
        {...register(`${questionId}`, {
          required: errorMessages.securityQuestion.valid,
          validate: {
            isDupe: (value) => {
              const questionsToValidateAgainst = [];

              let errorMsg = false;
              switch (questionId) {
                case 'IDToken1question': {
                  if (IDToken2question && IDToken2question !== '') questionsToValidateAgainst.push(IDToken2question);
                  if (IDToken3question && IDToken3question !== '') questionsToValidateAgainst.push(IDToken3question);

                  if (questionsToValidateAgainst.indexOf(value) >= 0) {
                    errorMsg = errorMessages.securityQuestion.duplicate;
                  }

                  break;
                }
                case 'IDToken2question': {
                  if (IDToken1question && IDToken1question !== '') questionsToValidateAgainst.push(IDToken1question);
                  if (IDToken3question && IDToken3question !== '') questionsToValidateAgainst.push(IDToken3question);

                  if (questionsToValidateAgainst.indexOf(value) >= 0) {
                    errorMsg = errorMessages.securityQuestion.duplicate;
                  }

                  break;
                }
                case 'IDToken3question': {
                  if (IDToken1question && IDToken1question !== '') questionsToValidateAgainst.push(IDToken1question);
                  if (IDToken2question && IDToken2question !== '') questionsToValidateAgainst.push(IDToken2question);

                  if (questionsToValidateAgainst.indexOf(value) >= 0) {
                    errorMsg = errorMessages.securityQuestion.duplicate;
                  }

                  break;
                }
                default:
              }

              return errorMsg || true;
            },
          },
        })}
      />
      <Controller
        name={answerId}
        control={control}
        render={() => (
          <TextField
            id={answerId}
            label={answerLabel}
            margin="normal"
            placeholder={placeholderAnswer}
            {...register(`${answerId}`, {
              minLength: {
                value: 2,
                message: errorMessages.securityQuestion.answer,
              },
              maxLength: {
                value: 130,
                message: errorMessages.securityQuestion.answer,
              },
              pattern: {
                value: /^[\d\s!'.?A-Za-z-]*$/,
                message: errorMessages.securityQuestion.answer,
              },
              required: errorMessages.securityQuestion.answer,
              validate: {
                isDupe: (value) => {
                  const answersToValidateAgainst = [];

                  let errorMsg = false;
                  switch (answerId) {
                    case 'IDToken1answer': {
                      if (IDToken2answer && IDToken2answer !== '') answersToValidateAgainst.push(IDToken2answer);
                      if (IDToken3answer && IDToken3answer !== '') answersToValidateAgainst.push(IDToken3answer);

                      if (answersToValidateAgainst.indexOf(value) >= 0) {
                        errorMsg = errorMessages.securityQuestion.answerDuplicate;
                      }

                      break;
                    }
                    case 'IDToken2answer': {
                      if (IDToken1answer && IDToken1answer !== '') answersToValidateAgainst.push(IDToken1answer);
                      if (IDToken3answer && IDToken3answer !== '') answersToValidateAgainst.push(IDToken3answer);

                      if (answersToValidateAgainst.indexOf(value) >= 0) {
                        errorMsg = errorMessages.securityQuestion.answerDuplicate;
                      }

                      break;
                    }
                    case 'IDToken3answer': {
                      if (IDToken1answer && IDToken1answer !== '') answersToValidateAgainst.push(IDToken1answer);
                      if (IDToken2answer && IDToken2answer !== '') answersToValidateAgainst.push(IDToken2answer);

                      if (answersToValidateAgainst.indexOf(value) >= 0) {
                        errorMsg = errorMessages.securityQuestion.answerDuplicate;
                      }

                      break;
                    }
                    default:
                  }

                  return errorMsg || true;
                },
                hasForbiddenWord: (value) => {
                  const { userId: newUserId } = newUserEnrollment;

                  const lowercasedUserId = userId?.toLowerCase() || newUserId?.toLowerCase();
                  const forbiddenWords = ['password', lowercasedUserId];
                  const formattedValue = value?.toLowerCase() || '';

                  let doesContainForbiddenWord = false;
                  for (const badWord of forbiddenWords) {
                    if (formattedValue?.indexOf(badWord) >= 0) {
                      doesContainForbiddenWord = true;
                    }
                  }

                  let errorMsg = false;
                  if (doesContainForbiddenWord) {
                    errorMsg = errorMessages.securityQuestion.answerForbiddenWords;
                  }

                  return errorMsg || true;
                },
              },
            })}
            error={!!errors[answerId]}
            helperText={errors[answerId]?.message}
            sx={{
              marginBottom: 0,
            }}
          />
        )}
      />
    </Box>
  );
};

SecurityQuestionFieldGroup.propTypes = {
  questionId: PropTypes.string.isRequired,
  answerId: PropTypes.string.isRequired,
  registrationValues: PropTypes.object,
};

export default SecurityQuestionFieldGroup;
